import { useRef, useState } from 'react';
import { Paperclip, Upload } from 'lucide-react';
import type { Id } from '@shared/types';
import { useUploadAttachment } from '@/lib/queries';
import { cn } from '@/lib/format';
import { ApiError } from '@/lib/api';

/**
 * Drag-and-drop / click-to-pick attachment upload.
 *
 * Uploads sequentially so a multi-file drop reports the first failure clearly
 * rather than firing a burst. The list refreshes via query invalidation, so
 * there's no optimistic placeholder to reconcile.
 */
export function AttachmentUpload({ taskId }: { taskId: Id }) {
  const upload = useUploadAttachment(taskId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    setError(null);
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : `„${file.name}" konnte nicht hochgeladen werden.`,
        );
        break; // stop on first failure so the message isn't clobbered
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-lg border border-dashed py-3 text-xs transition-colors',
          dragOver ? 'border-brand-400 bg-brand-50/60 text-brand-600 dark:bg-brand-900/20' : 'line text-muted hover:border-[var(--line-strong)] hover:text-body',
          upload.isPending && 'opacity-60',
        )}
      >
        {upload.isPending ? (
          <>
            <Upload className="size-3.5 animate-pulse" />
            Wird hochgeladen…
          </>
        ) : (
          <>
            <Paperclip className="size-3.5" />
            Datei anhängen — klicken oder hierher ziehen
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />
      {error && <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
